import React, { useRef, useState } from "react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Upload, Link2, Scissors, Wand2, X, Check, RotateCcw, Brush, Sparkles } from "lucide-react";

const CHECKER = {
  backgroundImage:
    "linear-gradient(45deg,#d7d2c6 25%,transparent 25%),linear-gradient(-45deg,#d7d2c6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#d7d2c6 75%),linear-gradient(-45deg,transparent 75%,#d7d2c6 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
  backgroundColor: "#fff",
};

export const ImageUploader = ({ value, onChange, onCommit }) => {
  const [phase, setPhase] = useState("source"); // 'source' | 'review'
  const [mode, setMode] = useState("file");
  const [file, setFile] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [preview, setPreview] = useState(value ? mediaUrl(value) : "");
  const [crop, setCrop] = useState(null);
  const dragRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // review state
  const [sid, setSid] = useState(null);
  const [origUrl, setOrigUrl] = useState("");
  const [prevUrl, setPrevUrl] = useState("");
  const [brush, setBrush] = useState("bg");
  const [brushSize, setBrushSize] = useState(24);
  const pending = useRef([]);
  const cur = useRef(null);
  const drawingRef = useRef(false);
  const canvasRef = useRef(null);
  const origImgRef = useRef(null);
  const srcWrapRef = useRef(null);

  /* ---------- source phase ---------- */
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setCrop(null); setPreview(URL.createObjectURL(f));
  };
  const loadUrl = () => {
    if (!urlInput.trim()) return;
    setFile(null); setCrop(null); setPreview(urlInput.trim());
  };
  const srcFrac = (e) => {
    const r = srcWrapRef.current.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const clamp01 = (v) => Math.max(0, Math.min(1, v));

  const beginNew = (e) => {
    if (!preview) return;
    e.preventDefault();
    const p = srcFrac(e);
    dragRef.current = { type: "new", ox: p.x, oy: p.y };
    setCrop({ x: p.x, y: p.y, w: 0, h: 0 });
    try { srcWrapRef.current.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const beginMove = (e) => {
    e.stopPropagation(); e.preventDefault();
    const p = srcFrac(e);
    dragRef.current = { type: "move", sx: p.x, sy: p.y, orig: { ...crop } };
    try { srcWrapRef.current.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const beginResize = (e, handle) => {
    e.stopPropagation(); e.preventDefault();
    dragRef.current = { type: "resize", handle, orig: { ...crop } };
    try { srcWrapRef.current.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const p = srcFrac(e);
    if (d.type === "new") {
      setCrop({ x: Math.min(d.ox, p.x), y: Math.min(d.oy, p.y), w: Math.abs(p.x - d.ox), h: Math.abs(p.y - d.oy) });
    } else if (d.type === "move") {
      const dx = p.x - d.sx, dy = p.y - d.sy;
      const nx = Math.max(0, Math.min(d.orig.x + dx, 1 - d.orig.w));
      const ny = Math.max(0, Math.min(d.orig.y + dy, 1 - d.orig.h));
      setCrop({ x: nx, y: ny, w: d.orig.w, h: d.orig.h });
    } else if (d.type === "resize") {
      let x1 = d.orig.x, y1 = d.orig.y, x2 = d.orig.x + d.orig.w, y2 = d.orig.y + d.orig.h;
      const H = d.handle, px = clamp01(p.x), py = clamp01(p.y);
      if (H.includes("w")) x1 = px;
      if (H.includes("e")) x2 = px;
      if (H.includes("n")) y1 = py;
      if (H.includes("s")) y2 = py;
      setCrop({ x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) });
    }
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.type === "new" && crop && (crop.w < 0.02 || crop.h < 0.02)) setCrop(null);
  };
  const HANDLES = ["nw", "ne", "sw", "se"];
  const handlePos = { nw: "left-0 top-0", ne: "right-0 top-0", sw: "left-0 bottom-0", se: "right-0 bottom-0" };
  const handleCursor = { nw: "nwse-resize", ne: "nesw-resize", sw: "nesw-resize", se: "nwse-resize" };

  const buildSource = (fd) => {
    if (mode === "file") { if (!file) { toast.error("Choose a file first"); return false; } fd.append("file", file); }
    else { if (!urlInput.trim()) { toast.error("Paste an image link first"); return false; } fd.append("url", urlInput.trim()); }
    if (crop) fd.append("crop", `${crop.x},${crop.y},${crop.w},${crop.h}`);
    return true;
  };

  const attachAsIs = async () => {
    const fd = new FormData();
    if (!buildSource(fd)) return;
    fd.append("remove_bg", "false");
    const hadCrop = !!crop;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      onChange(data.image); setPreview(mediaUrl(data.image) + "?t=" + Date.now());
      setFile(null); setCrop(null);
      if (onCommit) { await onCommit(data.image); toast.success(hadCrop ? "Cropped image saved to this ad" : "Image saved to this ad"); }
      else toast.success(hadCrop ? "Cropped image ready — press Save below to apply" : "Image ready — press Save below to apply");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const startBg = async () => {
    const fd = new FormData();
    if (!buildSource(fd)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bg/start", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setSid(data.sid);
      setOrigUrl(mediaUrl(data.original) + "?t=" + Date.now());
      setPrevUrl(mediaUrl(data.preview) + "?t=" + Date.now());
      pending.current = [];
      setPhase("review");
    } catch (e) { toast.error(e.response?.data?.detail || "Background removal failed"); }
    finally { setBusy(false); }
  };

  /* ---------- review phase: brushing ---------- */
  const setupCanvas = () => {
    const img = origImgRef.current, cv = canvasRef.current;
    if (!img || !cv) return;
    cv.width = img.clientWidth; cv.height = img.clientHeight;
    const ctx = cv.getContext("2d"); ctx.clearRect(0, 0, cv.width, cv.height);
  };
  const cFrac = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const strokeColor = () => (brush === "fg" ? "rgba(34,197,94,0.55)" : "rgba(217,56,30,0.55)");
  const dDown = (e) => {
    e.preventDefault();
    drawingRef.current = true; cur.current = [];
    const p = cFrac(e); cur.current.push([p.x, p.y]);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = strokeColor(); ctx.fillStyle = strokeColor();
    ctx.lineWidth = brushSize; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(p.x * canvasRef.current.width, p.y * canvasRef.current.height);
  };
  const dMove = (e) => {
    if (!drawingRef.current) return;
    const p = cFrac(e); cur.current.push([p.x, p.y]);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x * canvasRef.current.width, p.y * canvasRef.current.height); ctx.stroke();
  };
  const dUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (cur.current && cur.current.length) {
      pending.current.push({ mode: brush, r: brushSize / 2 / canvasRef.current.width, pts: cur.current });
    }
    cur.current = [];
  };

  const improve = async () => {
    if (!pending.current.length) { toast.error("Draw a few marks first — green = keep, red = background"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bg/refine", { sid, strokes: pending.current }, { timeout: 120000 });
      setPrevUrl(mediaUrl(data.preview) + "?t=" + Date.now());
      pending.current = [];
      setupCanvas();
      toast.success("Cutout improved from your marks");
    } catch (e) { toast.error("Could not refine"); }
    finally { setBusy(false); }
  };

  const keep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bg/commit", { sid }, { timeout: 120000 });
      onChange(data.image);
      setPreview(mediaUrl(data.image) + "?t=" + Date.now());
      setPhase("source"); setSid(null); setFile(null); setCrop(null);
      if (onCommit) { await onCommit(data.image); toast.success("Background removed and saved to this ad"); }
      else toast.success("Background removed — press Save below to apply");
    } catch (e) { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    if (sid) { try { await api.post("/admin/bg/cancel", { sid }); } catch (e) {} }
    setPhase("source"); setSid(null); pending.current = [];
    toast("Background removal discarded");
  };

  /* ---------- render ---------- */
  return (
    <div data-testid="image-uploader">
      <label className="pv-label">Advertisement image</label>

      {phase === "source" && (
        <>
          <div className="flex gap-2 mt-1 mb-3">
            {[["file", "Upload file", Upload], ["url", "Image link", Link2]].map(([m, label, Icon]) => (
              <button key={m} type="button" data-testid={`upload-mode-${m}`} onClick={() => setMode(m)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${mode === m ? "bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] border-[color:var(--c-ink)]" : "border-[color:var(--c-border)] hover:bg-[color:var(--c-surface)]"}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          {mode === "file" ? (
            <input type="file" accept="image/*" data-testid="upload-file-input" onChange={onFile}
              className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[color:var(--c-dark)] file:text-[color:var(--c-on-dark)] file:text-xs file:uppercase file:tracking-wider file:cursor-pointer bg-[color:var(--c-surface)] border border-[color:var(--c-border)] p-1" />
          ) : (
            <div className="flex gap-2">
              <Input data-testid="upload-url-input" value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/photo.jpg" className="rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)]" />
              <button type="button" data-testid="upload-url-load" onClick={loadUrl}
                className="whitespace-nowrap border border-[color:var(--c-ink)] px-4 text-xs uppercase tracking-wider hover:bg-[color:var(--c-dark)] hover:text-[color:var(--c-on-dark)] transition-colors">Preview</button>
            </div>
          )}

          {preview && (
            <div className="mt-3">
              <p className="text-xs text-[color:var(--c-ink2)] mb-1 inline-flex items-center gap-1"><Scissors size={12} /> Drag on the image to draw a crop, then drag it to reposition or pull a corner to resize</p>
              <div ref={srcWrapRef} data-testid="upload-crop-area" onPointerDown={beginNew} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
                className="relative inline-block max-w-full select-none cursor-crosshair border border-[color:var(--c-border)] bg-[color:var(--c-surface)] touch-none">
                <img src={preview} alt="preview" draggable={false} className="block max-h-72 w-auto pointer-events-none" />
                {crop && crop.w > 0 && crop.h > 0 && (
                  <div
                    data-testid="crop-box"
                    onPointerDown={beginMove}
                    className="absolute border-2 border-[color:var(--c-accent)] cursor-move"
                    style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%`, boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }}
                  >
                    {HANDLES.map((h) => (
                      <span
                        key={h}
                        data-testid={`crop-handle-${h}`}
                        onPointerDown={(e) => beginResize(e, h)}
                        className={`absolute ${handlePos[h]} w-3.5 h-3.5 bg-[color:var(--c-accent)] border border-white`}
                        style={{ transform: "translate(-50%, -50%)", cursor: handleCursor[h], marginLeft: h.includes("e") ? "50%" : 0, marginTop: h.includes("s") ? "50%" : 0 }}
                      />
                    ))}
                  </div>
                )}
                {crop && <button type="button" data-testid="upload-clear-crop" onClick={() => setCrop(null)} className="absolute top-1 right-1 z-10 bg-[color:var(--c-accent)] text-white text-[0.6rem] uppercase px-2 py-0.5 inline-flex items-center gap-1"><X size={11} /> clear</button>}
              </div>
              {crop && crop.w > 0.02 && crop.h > 0.02 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <button type="button" data-testid="crop-save" onClick={attachAsIs} disabled={busy}
                    className="inline-flex items-center gap-2 bg-[color:var(--c-accent)] text-white px-4 py-2.5 text-xs uppercase tracking-widest font-semibold hover:brightness-110 transition-[filter] disabled:opacity-60">
                    <Check size={14} /> {busy ? "Saving…" : "Save cropped image"}
                  </button>
                  <button type="button" data-testid="crop-reset" onClick={() => setCrop(null)} disabled={busy}
                    className="inline-flex items-center gap-2 border border-[color:var(--c-ink)] px-4 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--c-surface)] transition-colors disabled:opacity-60">
                    <X size={14} /> Reset crop
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <button type="button" data-testid="upload-attach" onClick={attachAsIs} disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--c-accent)] hover:text-white transition-colors disabled:opacity-60">
              <Check size={14} /> {busy ? "Working…" : (crop ? "Save cropped image" : "Save image")}
            </button>
            <button type="button" data-testid="upload-removebg-start" onClick={startBg} disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 border border-[color:var(--c-accent)] text-[color:var(--c-accent)] py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--c-accent)] hover:text-white transition-colors disabled:opacity-60">
              <Wand2 size={14} /> {busy ? "Working…" : "Remove background"}
            </button>
          </div>

          {!onCommit && (
            <p className="mt-2 text-xs text-[color:var(--c-accent)]" data-testid="upload-save-hint">
              After choosing an image, press the <b>Save</b> button at the bottom of the form to apply it to the ad.
            </p>
          )}

          {value && <p className="mt-2 text-xs text-[color:var(--c-ink2)] truncate" data-testid="upload-current-path">Current: {value}</p>}
        </>
      )}

      {phase === "review" && (
        <div className="mt-2" data-testid="bg-review">
          <div className="grid md:grid-cols-2 gap-4">
            {/* teach: mark on the original */}
            <div>
              <p className="pv-label mb-1 inline-flex items-center gap-1"><Brush size={12} /> Teach the tool</p>
              <div className="relative inline-block border border-[color:var(--c-border)] bg-[color:var(--c-surface)] leading-none">
                <img ref={origImgRef} src={origUrl} alt="original" draggable={false} onLoad={setupCanvas} className="block max-h-72 w-auto select-none pointer-events-none" />
                <canvas ref={canvasRef} data-testid="bg-brush-canvas" onMouseDown={dDown} onMouseMove={dMove} onMouseUp={dUp} onMouseLeave={dUp}
                  className="absolute inset-0 cursor-crosshair" style={{ width: "100%", height: "100%" }} />
              </div>
            </div>
            {/* preview cutout */}
            <div>
              <p className="pv-label mb-1 inline-flex items-center gap-1"><Sparkles size={12} /> Result preview</p>
              <div className="inline-block border border-[color:var(--c-border)] leading-none" style={CHECKER}>
                <img src={prevUrl} alt="cutout preview" data-testid="bg-preview-image" className="block max-h-72 w-auto" />
              </div>
            </div>
          </div>

          {/* brush controls */}
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <div className="flex gap-1">
              <button type="button" data-testid="brush-bg" onClick={() => setBrush("bg")}
                className={`inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-wider border ${brush === "bg" ? "bg-[color:var(--c-accent)] text-white border-[color:var(--c-accent)]" : "border-[color:var(--c-border)]"}`}>
                <span className="w-3 h-3 inline-block" style={{ background: "#D9381E" }} /> Mark background
              </button>
              <button type="button" data-testid="brush-fg" onClick={() => setBrush("fg")}
                className={`inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-wider border ${brush === "fg" ? "bg-[#16a34a] text-white border-[#16a34a]" : "border-[color:var(--c-border)]"}`}>
                <span className="w-3 h-3 inline-block" style={{ background: "#16a34a" }} /> Keep (foreground)
              </button>
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-[color:var(--c-ink2)]">
              Brush
              <input type="range" min="8" max="60" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} data-testid="brush-size" className="accent-[color:var(--c-accent)]" />
            </label>
          </div>
          <p className="text-xs text-[color:var(--c-ink2)] mt-2">Paint over any wrong areas, then <b>Improve</b>. The tool learns your marks and refines the cutout. When it looks right, <b>Keep result</b>.</p>

          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" data-testid="bg-improve" onClick={improve} disabled={busy}
              className="inline-flex items-center gap-2 border border-[color:var(--c-ink)] px-4 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--c-surface)] transition-colors disabled:opacity-60">
              <Wand2 size={14} /> {busy ? "Improving…" : "Improve with my marks"}
            </button>
            <button type="button" data-testid="bg-keep" onClick={keep} disabled={busy}
              className="inline-flex items-center gap-2 bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] px-4 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--c-accent)] hover:text-white transition-colors disabled:opacity-60">
              <Check size={14} /> Keep result
            </button>
            <button type="button" data-testid="bg-revert" onClick={revert} disabled={busy}
              className="inline-flex items-center gap-2 border border-[color:var(--c-accent)] text-[color:var(--c-accent)] px-4 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--c-accent)] hover:text-white transition-colors disabled:opacity-60">
              <RotateCcw size={14} /> Revert
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
